import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { CLIENT_API } from '../../api/injection-tokens/services.token';
import { ASSIGNMENT_API } from '../../api/services/assignment.service';
import { USER_SERVICE } from '../../api/services/user.service';
import { AccessService } from '../../core/services/access.service';
import { isRestrictedClient } from '../helpers/client-type.func';
import { NOTIFICATION_SERVICE } from '../notification/notification';
import { createErrorNotification } from '../notification/notification.factory';
import { TranslocoService } from '@jsverse/transloco';

export const restrictedClientGuard: CanActivateChildFn = async (child) => {
	const router = inject(Router);
	const api = inject(CLIENT_API);
	const assignmentApi = inject(ASSIGNMENT_API);
	const userApi = inject(USER_SERVICE);
	const access = inject(AccessService);
	const ntfy = inject(NOTIFICATION_SERVICE);
	const transloco = inject(TranslocoService);

	// We don't guard routes that are not 'client/:id'
	const path = child.routeConfig?.path ?? '';
	if (!path.startsWith('client/:id')) return true;

	// 'client/:id' is handle by the client resolver
	if (path === 'client/:id') return true;

	// Only "client/:id" routes will be guarded
	const id = Number(child.paramMap.get('id') ?? NaN);
	if (!Number.isFinite(id)) return router.createUrlTree(['']);

	try {
		const client = await api.getSingleWithRestricted(id);
		if (!client) return router.createUrlTree(['/desk']);
		if (isRestrictedClient(client)) {
			const [assignments, me] = await Promise.all([assignmentApi.getAll(id), userApi.getSingleMe()]);
			if (access.canAccessAdditionalAssignments(assignments, me)) {
				return true;
			}
			return router.createUrlTree(['/desk', 'client', id]);
		}
		return true;
	} catch {
		ntfy.addNotification(createErrorNotification(transloco.translate('base.notification_message.load_client_error')));

		return router.createUrlTree(['']);
	}
};
